import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Shield, Bug, FileSearch, AlertTriangle, Cpu, BarChart3, Crosshair, Radar, Zap, BookOpen } from 'lucide-react';
import TopBar from '../../components/TopBar';
import ContactFormModal from '../../components/ContactFormModal';
import PageTitle from '../../components/PageTitle';
import SiteFooter from '../../components/SiteFooter';

const capDefs = [
  { icon: Bug, key: 1, wide: true },
  { icon: FileSearch, key: 2, wide: false },
  { icon: AlertTriangle, key: 3, wide: false },
  { icon: Cpu, key: 4, wide: false },
  { icon: BarChart3, key: 5, wide: false },
  { icon: Shield, key: 6, wide: true },
];

const ucDefs = [
  { icon: Crosshair, key: 1, accent: 'rose' as const },
  { icon: Radar, key: 2, accent: 'sky' as const },
  { icon: Zap, key: 3, accent: 'amber' as const },
  { icon: BookOpen, key: 4, accent: 'violet' as const },
];

const accentMap = {
  rose: { border: 'border-l-rose-500', bg: 'bg-rose-500/10', text: 'text-rose-600 dark:text-rose-400' },
  sky: { border: 'border-l-sky-500', bg: 'bg-sky-500/10', text: 'text-sky-600 dark:text-sky-400' },
  amber: { border: 'border-l-amber-500', bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400' },
  violet: { border: 'border-l-violet-500', bg: 'bg-violet-500/10', text: 'text-violet-600 dark:text-violet-400' },
};

// --- Malware Analysis Demo ---
const MalwareDemo = () => {
  const { t } = useTranslation('products');
  const [activeTab, setActiveTab] = useState(0);
  const [progress, setProgress] = useState(0);
  const callStackRows = [
    { library: t('malware_analysis.demo_calls.0.library'), name: t('malware_analysis.demo_calls.0.name'), args: t('malware_analysis.demo_calls.0.args'), tone: 'danger', visibleAt: 0 },
    { library: t('malware_analysis.demo_calls.1.library'), name: t('malware_analysis.demo_calls.1.name'), args: t('malware_analysis.demo_calls.1.args'), tone: 'danger', visibleAt: 1 },
    { library: t('malware_analysis.demo_calls.2.library'), name: t('malware_analysis.demo_calls.2.name'), args: t('malware_analysis.demo_calls.2.args'), tone: 'danger', visibleAt: 1 },
    { library: t('malware_analysis.demo_calls.3.library'), name: t('malware_analysis.demo_calls.3.name'), args: t('malware_analysis.demo_calls.3.args'), tone: 'warning', visibleAt: 2 },
    { library: t('malware_analysis.demo_calls.4.library'), name: t('malware_analysis.demo_calls.4.name'), args: t('malware_analysis.demo_calls.4.args'), tone: 'warning', visibleAt: 2 },
    { library: t('malware_analysis.demo_calls.5.library'), name: t('malware_analysis.demo_calls.5.name'), args: t('malware_analysis.demo_calls.5.args'), tone: 'alert', visibleAt: 3 },
  ];

  useEffect(() => {
    setProgress(0);
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 5) { clearInterval(timer); return 5; }
        return prev + 1;
      });
    }, 600);
    return () => clearInterval(timer);
  }, [activeTab]);

  useEffect(() => {
    const rotation = setInterval(() => {
      setActiveTab(prev => (prev + 1) % 3);
    }, 7000);
    return () => clearInterval(rotation);
  }, []);

  const handleTabClick = useCallback((idx: number) => {
    setActiveTab(idx);
  }, []);

  const tabs = [
    t('malware_analysis.tab_callstack'),
    t('malware_analysis.tab_analysis'),
    t('malware_analysis.tab_network'),
  ];

  return (
    <div className="relative w-full max-w-4xl mx-auto rounded-xl border border-border bg-card shadow-2xl overflow-hidden text-left">
      {/* Window chrome */}
      <div className="flex items-center px-4 py-3 border-b border-border bg-muted/50">
        <div className="flex space-x-2">
          <div className="w-3 h-3 rounded-full bg-red-500/80" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
          <div className="w-3 h-3 rounded-full bg-green-500/80" />
        </div>
        <div className="mx-auto text-xs text-muted-foreground font-mono">trojan_agent.exe — {t('malware_analysis.demo_title_bar')}</div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 px-4 py-2 border-b border-border bg-muted/20">
        {tabs.map((label, i) => (
          <button
            key={i}
            onClick={() => handleTabClick(i)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${activeTab === i
              ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
          >
            {label}
          </button>
        ))}
        <div className="ml-auto">
          <span className="flex items-center gap-1.5 text-[10px] text-amber-500 font-mono">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            {t('malware_analysis.demo_scanning')}
          </span>
        </div>
      </div>

      {/* Content area */}
      <div className="relative h-[480px] overflow-hidden">

        {/* Tab 0: Malicious Call Stack + Pseudo C */}
        {activeTab === 0 && (
          <div className="p-4 md:p-6 pb-16 font-mono text-xs md:text-sm bg-background h-full relative animate-fade-in">
            <div className="text-[11px] text-muted-foreground/60 mb-3 uppercase tracking-wider">{t('malware_analysis.demo_critical_calls')}</div>
            <div className="space-y-0.5 text-foreground/80">
              {callStackRows.filter((row) => progress >= row.visibleAt).map((row) => (
                <div key={`${row.library}-${row.name}`} className="flex">
                  <span className="w-24 text-muted-foreground/40 select-none text-[11px]">{row.library}</span>
                  <span className={row.tone === 'danger' ? 'text-red-500 dark:text-red-400 font-semibold' : row.tone === 'warning' ? 'text-amber-500 dark:text-amber-400 font-semibold' : 'text-orange-500 dark:text-orange-400 font-semibold'}>{row.name}</span>
                  <span className="text-muted-foreground/50 ml-2">{row.args}</span>
                </div>
              ))}
            </div>

            {/* Pseudo-C decompilation */}
            {progress >= 3 && (
              <div className="mt-4 border-t border-border pt-3">
                <div className="text-[11px] text-muted-foreground/60 mb-2 uppercase tracking-wider">{t('malware_analysis.demo_pseudo_c')}</div>
                <div className="space-y-0.5 text-foreground/80">
                  <div><span className="text-purple-500">{t('malware_analysis.demo_code.returnType')}</span> <span className="text-blue-500">{t('malware_analysis.demo_code.functionName')}</span>(<span className="text-purple-500">{t('malware_analysis.demo_code.handleType')}</span> hProc) {'{'}</div>
                  <div className="pl-4"><span className="text-purple-500">{t('malware_analysis.demo_code.pointerType')}</span> {t('malware_analysis.demo_code.memoryAssign')} <span className="text-blue-500">{t('malware_analysis.demo_code.virtualAlloc')}</span>(hProc, <span className="text-orange-500">{t('malware_analysis.demo_code.null')}</span>, <span className="text-orange-500">{t('malware_analysis.demo_code.allocSize')}</span>,</div>
                  <div className="pl-16">{t('malware_analysis.demo_code.memoryFlags')});</div>
                  <div className="pl-4"><span className="text-blue-500">{t('malware_analysis.demo_code.writeProcessMemory')}</span>(hProc, mem, shellcode, <span className="text-orange-500">{t('malware_analysis.demo_code.shellcodeSize')}</span>, <span className="text-orange-500">{t('malware_analysis.demo_code.null')}</span>);</div>
                  <div className="pl-4"><span className="text-blue-500">{t('malware_analysis.demo_code.createRemoteThread')}</span>(hProc, <span className="text-orange-500">{t('malware_analysis.demo_code.null')}</span>, <span className="text-orange-500">{t('malware_analysis.demo_code.zero')}</span>, mem,</div>
                  <div className="pl-16"><span className="text-orange-500">{t('malware_analysis.demo_code.null')}</span>, <span className="text-orange-500">{t('malware_analysis.demo_code.zero')}</span>, <span className="text-orange-500">{t('malware_analysis.demo_code.null')}</span>);</div>
                  <div>{'}'}</div>
                </div>
              </div>
            )}

            {/* Detection badge */}
            {progress >= 4 && (
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-2 bg-red-500/10 text-red-600 dark:text-red-400 px-3 py-2 rounded-lg text-xs border border-red-500/20">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-semibold">{t('malware_analysis.demo_injection_detected')}</span>
                </div>
                <span className="text-[10px] bg-red-500/20 px-2 py-0.5 rounded font-mono">{t('malware_analysis.demo_severity')}</span>
              </div>
            )}
          </div>
        )}

        {/* Tab 1: AI Analysis */}
        {activeTab === 1 && (
          <div className="p-4 md:p-6 pb-16 text-sm bg-background h-full relative animate-fade-in">
            <div className="space-y-3">
              {progress >= 0 && (
                <div className="flex items-center gap-2 text-blue-500 dark:text-blue-400 text-xs font-medium">
                  <div className="w-4 h-4 border-2 border-blue-500/40 border-t-blue-500 rounded-full animate-spin" />
                  {t('malware_analysis.demo_ai_scanning')}
                </div>
              )}

              {progress >= 1 && (
                <div className="rounded-lg border border-border p-3 bg-muted/30">
                  <div className="text-[11px] text-muted-foreground/60 mb-1.5 uppercase tracking-wider">{t('malware_analysis.demo_family')}</div>
                  <div className="text-foreground font-semibold">{t('malware_analysis.demo_family_name')} <span className="text-red-500 text-xs ml-1">{t('malware_analysis.demo_family_version')}</span></div>
                  <div className="text-xs text-muted-foreground mt-1">{t('malware_analysis.demo_confidence')}: <span className="text-blue-500 font-medium">97.3%</span></div>
                </div>
              )}

              {progress >= 2 && (
                <div className="rounded-lg border border-border p-3 bg-muted/30">
                  <div className="text-[11px] text-muted-foreground/60 mb-1.5 uppercase tracking-wider">{t('malware_analysis.demo_mitre_title')}</div>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="px-2 py-0.5 rounded text-[11px] bg-red-500/10 text-red-600 dark:text-red-400 font-mono">{t('malware_analysis.demo_mitre_1')}</span>
                    <span className="px-2 py-0.5 rounded text-[11px] bg-amber-500/10 text-amber-600 dark:text-amber-400 font-mono">{t('malware_analysis.demo_mitre_2')}</span>
                    <span className="px-2 py-0.5 rounded text-[11px] bg-orange-500/10 text-orange-600 dark:text-orange-400 font-mono">{t('malware_analysis.demo_mitre_3')}</span>
                  </div>
                </div>
              )}

              {progress >= 3 && (
                <div className="rounded-lg border border-border p-3 bg-muted/30">
                  <div className="text-[11px] text-muted-foreground/60 mb-1.5 uppercase tracking-wider">{t('malware_analysis.demo_behavior_summary')}</div>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <div className="flex items-start gap-2"><span className="text-red-500 mt-0.5">●</span>{t('malware_analysis.demo_behavior_1')}</div>
                    <div className="flex items-start gap-2"><span className="text-amber-500 mt-0.5">●</span>{t('malware_analysis.demo_behavior_2')}</div>
                    <div className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">●</span>{t('malware_analysis.demo_behavior_3')}</div>
                  </div>
                </div>
              )}

              {progress >= 4 && (
                <div className="rounded-lg border border-red-500/20 p-3 bg-red-500/5">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-semibold text-red-600 dark:text-red-400">{t('malware_analysis.demo_verdict')}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{t('malware_analysis.demo_verdict_desc')}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 2: Network Features */}
        {activeTab === 2 && (
          <div className="p-4 md:p-6 pb-16 font-mono text-xs md:text-sm bg-background h-full relative animate-fade-in">
            <div className="text-[11px] text-muted-foreground/60 mb-3 uppercase tracking-wider">{t('malware_analysis.demo_network_ioc')}</div>
            <div className="space-y-2 text-foreground/80">
              {progress >= 0 && (
                <div className="rounded-lg border border-border p-2.5 bg-muted/20">
                  <div className="text-[10px] text-red-500 font-semibold mb-1">{t('malware_analysis.demo_c2_beacon')}</div>
                  <div className="flex flex-col gap-0.5">
                    <div><span className="text-muted-foreground/50">{t('malware_analysis.demo_dst_label')}</span> <span className="text-red-500 dark:text-red-400">{t('malware_analysis.demo_dst_value')}</span></div>
                    <div><span className="text-muted-foreground/50">{t('malware_analysis.demo_proto_label')}</span> {t('malware_analysis.demo_proto_value')} <span className="text-muted-foreground/50">{t('malware_analysis.demo_interval_label')}</span> <span className="text-orange-500">{t('malware_analysis.demo_interval_value')}</span></div>
                    <div><span className="text-muted-foreground/50">{t('malware_analysis.demo_sni_label')}</span> <span className="text-amber-500">{t('malware_analysis.demo_sni_value')}</span></div>
                  </div>
                </div>
              )}

              {progress >= 1 && (
                <div className="rounded-lg border border-border p-2.5 bg-muted/20">
                  <div className="text-[10px] text-amber-500 font-semibold mb-1">{t('malware_analysis.demo_dns_exfil')}</div>
                  <div className="flex flex-col gap-0.5">
                    <div><span className="text-muted-foreground/50">{t('malware_analysis.demo_query_label')}</span> <span className="text-amber-500 dark:text-amber-400">{t('malware_analysis.demo_query_value')}</span></div>
                    <div><span className="text-muted-foreground/50">{t('malware_analysis.demo_type_label')}</span> {t('malware_analysis.demo_type_value')} <span className="text-muted-foreground/50">{t('malware_analysis.demo_encoding_label')}</span> {t('malware_analysis.demo_encoding_value')}</div>
                  </div>
                </div>
              )}

              {progress >= 2 && (
                <div className="rounded-lg border border-border p-2.5 bg-muted/20">
                  <div className="text-[10px] text-blue-500 font-semibold mb-1">{t('malware_analysis.demo_http_callback')}</div>
                  <div className="flex flex-col gap-0.5">
                    <div><span className="text-muted-foreground/50">{t('malware_analysis.demo_post_label')}</span> <span className="text-blue-500">{t('malware_analysis.demo_post_value')}</span></div>
                    <div><span className="text-muted-foreground/50">{t('malware_analysis.demo_ua_label')}</span> <span className="text-foreground/60">{t('malware_analysis.demo_ua_value')}</span></div>
                    <div><span className="text-muted-foreground/50">{t('malware_analysis.demo_cookie_label')}</span> <span className="text-orange-500">{t('malware_analysis.demo_cookie_value')}</span></div>
                  </div>
                </div>
              )}

              {progress >= 3 && (
                <div className="rounded-lg border border-border p-2.5 bg-muted/20">
                  <div className="text-[10px] text-purple-500 font-semibold mb-1">{t('malware_analysis.demo_generated_signatures')}</div>
                  <div className="flex flex-col gap-0.5">
                    <div><span className="text-muted-foreground/50">{t('malware_analysis.demo_yara_label')}</span> <span className="text-purple-500">{t('malware_analysis.demo_yara_value')}</span></div>
                    <div><span className="text-muted-foreground/50">{t('malware_analysis.demo_snort_label')}</span> <span className="text-purple-500">{t('malware_analysis.demo_snort_value')}</span></div>
                  </div>
                </div>
              )}
            </div>

            {progress >= 4 && (
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-2 bg-blue-500/10 text-blue-600 dark:text-blue-400 px-3 py-2 rounded-lg text-xs border border-blue-500/20">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  <span className="font-semibold">{t('malware_analysis.demo_ioc_extracted')}</span>
                </div>
                <span className="text-[10px] bg-blue-500/20 px-2 py-0.5 rounded font-mono">{t('malware_analysis.demo_ioc_count')}</span>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default function MalwareAnalysis() {
  const { t } = useTranslation('products');
  const [showContactForm, setShowContactForm] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <PageTitle 
        titleKey="malware_analysis.title" 
        namespace="products" 
        descriptionKey="malware_analysis.subtitle"
      />
      <TopBar />

      {/* Hero */}
      <section className="relative pt-24 pb-5 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/[0.03] via-transparent to-transparent dark:from-blue-500/[0.06]" />
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(59,130,246,0.07) 1px, transparent 0)', backgroundSize: '32px 32px' }} />

        {/* Floating decorative orbs */}
        <div className="absolute top-16 left-[8%] w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-4 right-[10%] w-56 h-56 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

        {/* Decorative shield illustration - left */}
        <svg className="absolute left-0 top-1/2 -translate-y-1/2 w-48 h-64 text-blue-500/10 hidden lg:block" viewBox="0 0 192 256" fill="none">
          <path d="M160 40 L120 40 L96 64 L72 40 L32 40 L32 140 L96 200 L160 140 Z" stroke="currentColor" strokeWidth="1.5" />
          <path d="M140 56 L108 56 L96 68 L84 56 L52 56 L52 128 L96 172 L140 128 Z" stroke="currentColor" strokeWidth="1" strokeDasharray="4 3" />
          <circle cx="96" cy="110" r="16" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="96" cy="110" r="6" fill="currentColor" className="animate-pulse" />
        </svg>

        {/* Decorative hex pattern - right */}
        <svg className="absolute right-4 top-1/2 -translate-y-1/2 w-44 h-64 text-purple-500/10 hidden lg:block" viewBox="0 0 176 256" fill="none">
          <path d="M88 20 L138 48 L138 104 L88 132 L38 104 L38 48 Z" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="88" cy="84" r="4" fill="currentColor" className="animate-pulse" />
          <path d="M88 140 L138 168 L138 224 L88 252 L38 224 L38 168 Z" stroke="currentColor" strokeWidth="1" opacity="0.5" />
          <circle cx="88" cy="196" r="3" fill="currentColor" className="animate-pulse" style={{ animationDelay: '1s' }} />
        </svg>

        <div className="relative max-w-4xl mx-auto text-center">
          {/* Icon */}
          <div className="inline-flex h-16 w-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 items-center justify-center mb-6 shadow-lg shadow-blue-500/20">
            <Shield className="w-8 h-8 text-white" />
          </div>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter mb-4 whitespace-nowrap">
            <span className="bg-gradient-to-r from-blue-500 via-purple-500 to-blue-600 bg-clip-text text-transparent">
              {t('malware_analysis.name')}
            </span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground font-medium mb-8">{t('malware_analysis.subtitle')}</p>

          <p className="text-lg text-foreground/80 leading-relaxed max-w-3xl mx-auto mb-4">
            {t('malware_analysis.desc_p1')}
          </p>
          <p className="text-base text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8">
            {t('malware_analysis.desc_p2')}
          </p>

          <div className="flex justify-center flex-wrap gap-4">
            <button
              onClick={() => setShowContactForm(true)}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 hover:scale-[1.02] active:scale-[0.98]"
            >
              {t('malware_analysis.cta')}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Live Malware Analysis Demo */}
      <section className="pt-5 pb-10 px-4 sm:px-6 lg:px-8">

        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3 text-center">{t('malware_analysis.demo_title')}</h2>
          <p className="text-muted-foreground text-center mb-6 max-w-2xl mx-auto">
            {t('malware_analysis.demo_desc')}
          </p>
          <MalwareDemo />
        </div>
      </section>

      {/* Core Capabilities Bento Grid */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3 text-center">{t('malware_analysis.capabilities_title')}</h2>
          <p className="text-muted-foreground text-center mb-6 max-w-2xl mx-auto">
            {t('malware_analysis.capabilities_desc')}
          </p>

          {/* Pipeline indicator */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-1 mb-6">
            {(['cap_step1', 'cap_step2', 'cap_step3', 'cap_step4'] as const).map((key, i) => (
              <React.Fragment key={key}>
                {i > 0 && <div className="hidden sm:block w-6 h-px bg-blue-500/30" />}
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-medium">
                  <span className="w-4 h-4 rounded-full bg-blue-500/20 flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
                  {t(`malware_analysis.${key}`)}
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {capDefs.map(({ icon: Icon, key, wide }) => (
              <div
                key={key}
                className={`rounded-xl border border-border bg-card overflow-hidden transition-all hover:border-blue-500/30 ${wide ? 'md:col-span-2' : ''
                  } ${key === 1 ? 'bg-gradient-to-br from-blue-500/[0.04] to-purple-500/[0.04]' : ''}`}
              >
                <div className="p-6 flex items-start gap-4">
                  <div className={`shrink-0 rounded-lg flex items-center justify-center ${key === 1
                    ? 'h-11 w-11 bg-gradient-to-br from-blue-500 to-purple-600'
                    : key === 6
                      ? 'h-11 w-11 bg-gradient-to-br from-blue-500/20 to-purple-500/20'
                      : 'h-10 w-10 bg-blue-500/10'
                    }`}>
                    <Icon className={`w-5 h-5 ${key === 1 ? 'text-white' : 'text-blue-500'}`} />
                  </div>
                  <div className="min-w-0">
                    <h3 className={`font-semibold text-foreground tracking-tight mb-1.5 ${key === 1 || key === 6 ? 'text-lg' : 'text-base'}`}>
                      {t(`malware_analysis.cap${key}_title`)}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(`malware_analysis.cap${key}_desc`)}</p>
                    {key === 6 && (
                      <div className="flex items-center gap-3 mt-3 text-xs text-blue-600 dark:text-blue-400 font-mono">
                        <span className="bg-blue-500/10 px-2 py-0.5 rounded">{t('malware_analysis.demo_pipeline_ingest')}</span>
                        <span className="text-muted-foreground/30">→</span>
                        <span className="bg-blue-500/10 px-2 py-0.5 rounded">{t('malware_analysis.demo_pipeline_analyze')}</span>
                        <span className="text-muted-foreground/30">→</span>
                        <span className="bg-blue-500/10 px-2 py-0.5 rounded">{t('malware_analysis.demo_pipeline_classify')}</span>
                        <span className="text-muted-foreground/30">→</span>
                        <span className="bg-blue-500/10 px-2 py-0.5 rounded">{t('malware_analysis.demo_pipeline_report')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3 text-center">{t('malware_analysis.usecases_title')}</h2>
          <p className="text-muted-foreground text-center mb-6 max-w-2xl mx-auto">
            {t('malware_analysis.usecases_desc')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {ucDefs.map(({ icon: Icon, key, accent }) => {
              const c = accentMap[accent];
              return (
                <div key={key} className={`rounded-xl border border-border ${c.border} border-l-[3px] bg-card p-5 transition-all hover:border-l-4`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`h-8 w-8 rounded-lg ${c.bg} flex items-center justify-center shrink-0`}>
                      <Icon className={`w-4 h-4 ${c.text}`} />
                    </div>
                    <h3 className="font-semibold text-foreground tracking-tight">{t(`malware_analysis.uc${key}_title`)}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">{t(`malware_analysis.uc${key}_desc`)}</p>
                  <span className={`inline-block text-[11px] ${c.bg} ${c.text} px-2 py-0.5 rounded-full font-medium`}>
                    {t(`malware_analysis.uc${key}_tag`)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <SiteFooter />

      <ContactFormModal isOpen={showContactForm} onClose={() => setShowContactForm(false)} product="malware_analysis" />
    </div>
  );
}
